import { useMsal } from '@azure/msal-react';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiCreationRequestedEvent,
    RoiDeletionRequestedEvent,
    StructureSetApprovalChangeRequestedEvent,
    StructureSetDescriptionChangeRequestedEvent,
    StructureSetLabelChangeRequestedEvent,
    StructureSetVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { environmentState } from '~/state/atoms/engineAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';

import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * Hook for managing structure set operations via domain events.
 *
 * Provides functions to modify structure set properties and ROIs by publishing domain events
 * to the event bus. All operations are handled asynchronously through the event-driven architecture.
 * The actual state updates are performed by event handlers, maintaining separation of concerns.
 *
 * @returns Structure set domain event publishing functions.
 */
export default function useStructureSet() {
    const [currentStructureSet, setCurrentStructureSet] = useRecoilState(
        currentStructureSetState
    );
    const setSelectedROI = useSetRecoilState(selectedROIState);
    const env = useRecoilValue(environmentState);
    const sessionData = useRecoilValue(sessionDataAtom);
    const { instance } = useMsal();
    const session = useSessionOrNull();

    const setStructureSetLabel = useCallback(
        async (newLabel: string) => {
            if (currentStructureSet) {
                try {
                    await domainEventBus.publish(
                        new StructureSetLabelChangeRequestedEvent(
                            currentStructureSet.dicomUid,
                            newLabel,
                            'user'
                        )
                    );
                } catch (error) {
                    console.error(
                        `Error updating structure set label: ${error}`
                    );
                }
            }
        },
        [currentStructureSet]
    );

    const setStructureSetDescription = useCallback(
        async (newDescription: string) => {
            if (currentStructureSet) {
                try {
                    await domainEventBus.publish(
                        new StructureSetDescriptionChangeRequestedEvent(
                            currentStructureSet.dicomUid,
                            newDescription,
                            'user'
                        )
                    );
                } catch (error) {
                    console.error(
                        `Error updating structure set description: ${error}`
                    );
                }
            }
        },
        [currentStructureSet]
    );

    const setStructureSetVisibility = useCallback(
        async (newVisibility: boolean) => {
            if (currentStructureSet) {
                try {
                    await domainEventBus.publish(
                        new StructureSetVisibilityChangeRequestedEvent(
                            currentStructureSet.dicomUid,
                            newVisibility,
                            'user'
                        )
                    );
                } catch (error) {
                    console.error(`Error updating visibility: ${error}`);
                }
            }
        },
        [currentStructureSet]
    );

    const setStructureSetApprovalStatus = useCallback(
        async (newStatus: DICOMApprovalStatus, newReviewerName?: string) => {
            if (currentStructureSet) {
                try {
                    const activeMsalAccount =
                        instance?.getActiveAccount()?.name;
                    const reviewer =
                        newReviewerName ??
                        sessionData?.username ??
                        activeMsalAccount ??
                        'unknown';

                    await domainEventBus.publish(
                        new StructureSetApprovalChangeRequestedEvent(
                            currentStructureSet.dicomUid,
                            newStatus,
                            reviewer,
                            'user'
                        )
                    );
                } catch (error) {
                    console.error(`Error updating approval status: ${error}`);
                }
            }
        },
        [currentStructureSet, sessionData, instance]
    );

    const createRoi = useCallback(
        async (interpretedType?: RoiInterpretedType) => {
            if (currentStructureSet) {
                try {
                    const initialCoordinatesMm =
                        interpretedType &&
                        ROI_POINT_TYPES.includes(interpretedType) &&
                        env
                            ? env.planeIntersectionPoint.coordinates
                            : undefined;

                    await domainEventBus.publish(
                        new RoiCreationRequestedEvent(
                            currentStructureSet.dicomUid,
                            'user',
                            interpretedType,
                            initialCoordinatesMm
                                ? {
                                      x: initialCoordinatesMm[0],
                                      y: initialCoordinatesMm[1],
                                      z: initialCoordinatesMm[2],
                                  }
                                : undefined
                        )
                    );
                } catch (error) {
                    console.error(`Error creating a new ROI: ${error}`);
                    enqueueSnackbar(`Error creating a new ROI: ${error}`, {
                        variant: 'warning',
                    });
                }
            }
        },
        [currentStructureSet, env]
    );

    const deleteRoi = useCallback(
        async (roiNumber: number) => {
            if (currentStructureSet) {
                try {
                    await domainEventBus.publish(
                        new RoiDeletionRequestedEvent(
                            currentStructureSet.dicomUid,
                            roiNumber,
                            'user'
                        )
                    );
                } catch (error) {
                    console.error(`Error deleting ROI: ${error}`);
                    enqueueSnackbar(`Error deleting ROI: ${error}`, {
                        variant: 'warning',
                    });
                }
            }
        },
        [currentStructureSet]
    );

    const isStructureSetVisible = useMemo(
        () =>
            currentStructureSet?.roiArray
                .map((roi) => roi.visible)
                .some((visible) => visible),
        [currentStructureSet]
    );

    const createNewRoi = useCallback(
        async (
            structureSetUid: string,
            structureName: string,
            interpretedType?: RoiInterpretedType,
            selectRoiOnCreation = true
        ) => {
            try {
                if (!currentStructureSet?.dicomUid) {
                    console.warn(
                        'No structure set UID available for ROI creation'
                    );
                    return;
                }

                const structureSet =
                    session?.patient.getStructureSetWithSopInstanceUid(
                        structureSetUid
                    );
                if (!structureSet) {
                    console.warn(`Structure set not found: ${structureSetUid}`);
                    return;
                }

                const structureSetService = session?.structureSetService;
                if (!structureSetService) {
                    console.warn(
                        'No structure set service available for ROI creation'
                    );
                    return;
                }

                // Route through the tracked service so the creation
                // emits a 'created' changeset (projection layer and
                // persistence stay in sync).
                const newRoi = structureSetService.createRoi(
                    { id: structureSet.id },
                    interpretedType,
                    structureName
                );
                setCurrentStructureSet(
                    createStructureSetReadModel(structureSet)
                );

                // EngineAdapter publishes RoiCreatedEvent from the tracked
                // 'created' changeset emitted by createRoi above; a
                // handler-side publish would double-create the engine ROI.
                if (selectRoiOnCreation) {
                    setSelectedROI(newRoi.roiNumber);
                }

                return newRoi;
            } catch (error) {
                console.error(`Failed to handle ROI creation:`, error);
            }
        },
        [
            currentStructureSet?.dicomUid,
            session,
            setCurrentStructureSet,
            setSelectedROI,
        ]
    );

    return {
        structureSet: currentStructureSet,
        isStructureSetVisible,
        setStructureSetLabel,
        setStructureSetDescription,
        setStructureSetApprovalStatus,
        setStructureSetVisibility,
        createNewRoi,
        createRoi,
        deleteRoi,
    };
}
